import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Flex,
  Link,
  TabList,
  Tabs,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useWeb3React } from '@web3-react/core'
import { CoingeckoPricesResData } from 'modules/coingecko'
import {
  ActiveTokenOffer,
  DetailedOrder,
  TokenOfferType,
} from 'modules/offer/types'
import {
  parseBuyOrdersWithPrice,
  parseMyOrdersWithPrice,
  parseSellOrdersWithPrice,
} from 'modules/order/helpers'
import { getNetworkTokens } from 'modules/token/networkCuratedTokens'
import { Token } from 'modules/token/types'
import NextLink from 'next/link'
import { isAddressContained, isAddressEqual } from 'utils/web3'

import { Button } from 'components/Buttons'
import ActiveTokenOffersTable from 'components/Tables/ActiveTokenOffersTable'
import { OrderTypeTab } from 'components/Tabs'

interface ITokenOrders {
  chainId: number
  orders: DetailedOrder[]
  token: Token
  lvts: Token[]
  tabType?: string
  prices: CoingeckoPricesResData
}

const TokenOrders = ({
  chainId,
  token,
  lvts,
  orders,
  tabType,
  prices,
}: ITokenOrders) => {
  const { account } = useWeb3React()
  const unlockedTokenSymbol = token.symbol
  const unlockedTokenAddress = token.address
  const selectedLvtSymbol =
    lvts.find((x) => isAddressEqual(x.address, tabType))?.symbol || ''

  // Tabs
  const [tabIndex, setTabIndex] = useState<number>(0)
  const handleTabsChange = (index: number) => {
    setTabIndex(index)
  }
  useEffect(() => {
    if (!account) {
      setTabIndex(0)
    }
  }, [account])

  // Table data
  const { buyData, sellData, userData } = useMemo(() => {
    // 1. Declare arrays for Tabs
    const buyData: ActiveTokenOffer[] = []
    const sellData: ActiveTokenOffer[] = []
    const userData: ActiveTokenOffer[] = []

    // 1.1. Helper stable coins array
    const curatedStablecoins = getNetworkTokens(chainId, 'stables').map(
      (x) => x.address
    )
    // 1.2. Helper target token
    const targetToken =
      tabType === 'all' || tabType === 'unlocked' ? token.address : tabType

    orders
      .filter((x) => {
        // 2. Filters the orders according to the selected token
        // 2.1. Filters all tokens
        if (tabType === 'all') return true
        // 2.2. Filters just the unlocked token
        if (tabType === 'unlocked') {
          return isAddressContained(unlockedTokenAddress, [
            x.buyToken.address,
            x.sellToken.address,
          ])
        }
        // 2.3. Filters just the selected LVT
        return isAddressContained(tabType, [
          x.buyToken.address,
          x.sellToken.address,
        ])
      })
      .forEach((order) => {
        // 3. Handles the order
        // 3.1. Add user's orders (if connected)
        if (!!account && isAddressEqual(order.maker, account)) {
          const parsedOrder = parseMyOrdersWithPrice(
            order,
            curatedStablecoins,
            prices,
            targetToken
          )

          userData.push(parsedOrder)
        } else {
          /**
           * @dev
           * Beware that the concept of an order sellToken doesn't mean that
           * the user will sell his token. It means that the order is selling that token,
           * which implies that the user that is seeing the placed order will buy it.
           */
          // 3.2. Add buy orders (using the sellToken, because the user will buy)
          if (
            tabType === 'all'
              ? isAddressEqual(order.sellToken.address, unlockedTokenAddress) ||
                isAddressEqual(order.sellTokenParent, unlockedTokenAddress)
              : tabType === 'unlocked'
              ? isAddressEqual(order.sellToken.address, unlockedTokenAddress)
              : isAddressEqual(order.sellToken.address, tabType)
          ) {
            const parsedOrder = parseBuyOrdersWithPrice(
              order,
              curatedStablecoins,
              prices
            )
            buyData.push(parsedOrder)
          }
          // 3.3 Add sell orders (using the buyToken, because the user will sell)
          if (
            tabType === 'all'
              ? isAddressEqual(order.buyToken.address, unlockedTokenAddress) ||
                isAddressEqual(order.buyTokenParent, unlockedTokenAddress)
              : tabType === 'unlocked'
              ? isAddressEqual(order.buyToken.address, unlockedTokenAddress)
              : isAddressEqual(order.buyToken.address, tabType)
          ) {
            const parsedOrder = parseSellOrdersWithPrice(
              order,
              curatedStablecoins,
              prices
            )
            sellData.push(parsedOrder)
          }
        }
      })

    // 3. Return arrays
    return { buyData, sellData, userData }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, tabType, account, prices])

  const newTradeParams = useMemo(() => {
    const address =
      tabType === 'all' || tabType === 'unlocked' ? token.address : tabType
    return `sell=${address}&network=${chainId}`
  }, [token, chainId, tabType])

  return (
    <Box>
      {/* Order type selector and create order button */}
      <Flex
        justifyContent={{ base: 'center', md: 'space-between' }}
        mt={{ base: 4, md: 8 }}
        pb={4}
        direction={{ base: 'column-reverse', md: 'row' }}
        alignItems={{ base: 'flex-start', sm: 'center' }}
        px={{ base: 5, sm: 8, md: 0 }}
        gap={5}
        flexWrap="wrap"
        overflowX="auto"
      >
        {/* Order type selector */}
        <Tabs variant="unstyled" onChange={handleTabsChange}>
          <TabList
            borderRadius={'xl'}
            fontFamily={'mono'}
            _focus={{ boxShadow: 'none' }}
          >
            <OrderTypeTab
              isStart
              title={`Buy ${selectedLvtSymbol}`}
              counter={buyData.length}
              isSelected={tabIndex === 0}
              minW={{ base: account ? 24 : 32, sm: 32, md: 32, lg: 40 }}
            />
            <OrderTypeTab
              isEnd={!account}
              title={`Sell ${selectedLvtSymbol}`}
              counter={sellData.length}
              isSelected={tabIndex === 1}
              minW={{ base: account ? 24 : 32, sm: 32, md: 32, lg: 40 }}
            />
            {!!account && (
              <OrderTypeTab
                isEnd
                title={`My Orders`}
                counter={userData.length}
                isSelected={tabIndex === 2}
              />
            )}
          </TabList>
        </Tabs>
        {/* Create order button */}
        <NextLink href={`/trade?${newTradeParams}`} passHref>
          <Link _hover={{ textDecoration: 'none' }} mb={1}>
            <Button
              borderRadius={'xl'}
              bg={'brand'}
              size={'xl'}
              fontFamily={'mono'}
              boxShadow={'0px 3px 0px rgba(70, 126, 229, 0.6)'}
              _focus={{ boxShadow: 'none' }}
              color={'white'}
              px={6}
              py={4}
            >
              {useBreakpointValue({
                base: '+ Create Buy or Sell Order',
                md: '+ Create New Buy or Sell Order',
              })}
            </Button>
          </Link>
        </NextLink>
      </Flex>
      <Box
        pb={16}
        overflowX={{ base: 'scroll', sm: 'auto' }}
        css={{
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: '-ms-autohiding-scrollbar',
        }}
      >
        {tabIndex === 0 ? (
          <ActiveTokenOffersTable
            type={TokenOfferType.Buy}
            data={buyData}
            chainId={chainId}
            selectedToken={
              tabType === 'all'
                ? `${unlockedTokenSymbol}${
                    lvts.length > 0 ? ' or its LVTs' : ''
                  }`
                : selectedLvtSymbol || unlockedTokenSymbol
            }
          />
        ) : tabIndex === 1 ? (
          <ActiveTokenOffersTable
            type={TokenOfferType.Sell}
            data={sellData}
            chainId={chainId}
            selectedToken={
              tabType === 'all'
                ? `${unlockedTokenSymbol}${
                    lvts.length > 0 ? ' or its LVTs' : ''
                  }`
                : selectedLvtSymbol || unlockedTokenSymbol
            }
          />
        ) : (
          <ActiveTokenOffersTable
            type={TokenOfferType.User}
            data={userData}
            chainId={chainId}
            selectedToken={
              tabType === 'all'
                ? `${unlockedTokenSymbol}${
                    lvts.length > 0 ? ' or its LVTs' : ''
                  }`
                : selectedLvtSymbol || unlockedTokenSymbol
            }
          />
        )}
      </Box>
    </Box>
  )
}

export default TokenOrders
